import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-location';
import { useState, useEffect } from 'react';
import ErrorBox from '../../core/components/ErrorBox';
import PageContainer from '../../core/components/PageContainer';
import { usePropertyId } from '../../core/hooks/usePropertyId';
import { apiRawFetch } from '../../main/client';
import PropertySelectionDialog from '../../property/components/PropertySelectionDialog';
import TaxBreakdownCard from './TaxBreakdownCard';
import TaxConfigurationPrompt from './TaxConfigurationPrompt';
import TaxPaymentStatusCard from './TaxPaymentStatusCard';

interface TaxComponentResult {
    id: string;
    name: string;
    category: 'indirect' | 'income' | 'social' | 'other';
    amount: number;
    rate: number;
}

interface TaxPresetSummary {
    id: string;
    name: string;
    country: string;
    entityType: 'individual' | 'company';
    description: string;
}

interface TaxBreakdownData {
    preset: TaxPresetSummary;
    grossRevenue: number;
    breakdown: TaxComponentResult[];
    totalsByCategory: Record<string, number>;
    totalTaxes: number;
    netIncome: number;
    effectiveRate: number;
    currency: string;
    bookingsCount: number;
    daysHosted: number;
    year: number;
}

interface DashboardData {
    period: {
        year: number;
        month: number | null;
        startDate: string;
        endDate: string;
    };
    revenue: {
        totalIncome: number;
        bookingRevenue: number;
    };
    expenses: {
        totalExpenses: number;
        byCategory: {
            categoryId: number;
            name: string;
            type: string;
            amount: number;
        }[];
    };
    netIncome: number;
    bookings: {
        count: number;
        nightsHosted: number;
        avgRevenuePerNight: number;
        occupancyRate: number;
    };
    taxSummary: TaxBreakdownData | null;
    currency: string;
}

const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount);
};

const MetricCard = ({
    title,
    value,
    subtitle,
    icon,
    color,
    large,
}: {
    title: string;
    value: string;
    subtitle?: string;
    icon: React.ReactNode;
    color: string;
    large?: boolean;
}) => (
    <Card
        sx={{
            border: `3px solid ${color}`,
            borderRadius: 0,
            backgroundColor: '#1a1a2e',
            height: '100%',
        }}
    >
        <CardContent sx={{ p: large ? 4 : 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                    <Typography
                        variant="overline"
                        sx={{
                            color: 'text.secondary',
                            fontWeight: 700,
                            fontSize: large ? '0.875rem' : '0.75rem',
                            letterSpacing: '0.1em',
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 900,
                            color,
                            fontSize: large ? '2.5rem' : '1.75rem',
                            mt: 0.5,
                        }}
                    >
                        {value}
                    </Typography>
                    {subtitle && (
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                mt: 0.5,
                                fontSize: '0.875rem',
                            }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <Box
                    sx={{
                        p: large ? 2 : 1.5,
                        border: `2px solid ${color}`,
                        backgroundColor: `${color}20`,
                    }}
                >
                    {icon}
                </Box>
            </Stack>
        </CardContent>
    </Card>
);

const FinancialDashboard = () => {
    const propertyId = usePropertyId();
    const navigate = useNavigate();
    const pathname = window.location.hash.replace('#', '');

    const isLegacyRoute = pathname === '/financial';
    const isPropertyRoute = /^\/\d+\/financial/.test(pathname);

    const [year, setYear] = useState(new Date().getFullYear());
    const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

    useEffect(() => {
        if (!propertyId) return;

        const fetchDashboard = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await apiRawFetch(
                    `api/properties/${propertyId}/financial/dashboard?year=${year}`
                );

                if (!response.ok) {
                    throw new Error('Failed to fetch financial dashboard');
                }

                const { data } = await response.json();
                setDashboardData(data);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Unknown error');
            } finally {
                setLoading(false);
            }
        };

        fetchDashboard();
    }, [propertyId, year]);

    if (!propertyId) {
        if (isPropertyRoute) {
            return null;
        }
        if (isLegacyRoute) {
            return <PropertySelectionDialog open={true} targetPath="/financial" />;
        }
        return null;
    }

    if (error) {
        return <ErrorBox error={new Error(error)} />;
    }

    return (
        <PageContainer>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 4,
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Typography
                    variant="h2"
                    component="h1"
                    sx={{
                        fontSize: '2rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        fontWeight: 900,
                    }}
                >
                    Financial Dashboard
                </Typography>

                <Stack direction="row" spacing={2} alignItems="center">
                    <FormControl
                        size="small"
                        sx={{
                            minWidth: 120,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 0,
                                '& fieldset': {
                                    borderWidth: '2px',
                                    borderColor: '#374151',
                                },
                                '&:hover fieldset': {
                                    borderWidth: '2px',
                                    borderColor: '#6366f1',
                                },
                                '&.Mui-focused fieldset': {
                                    borderWidth: '3px',
                                    borderColor: '#6366f1',
                                },
                            },
                        }}
                    >
                        <Select
                            value={year}
                            onChange={(e) => setYear(Number(e.target.value))}
                            sx={{
                                fontWeight: 700,
                                textTransform: 'uppercase',
                            }}
                        >
                            {years.map((y) => (
                                <MenuItem key={y} value={y}>
                                    {y}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </Box>

            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 8,
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : dashboardData ? (
                <Stack spacing={4}>
                    {/* Net Income - Primary Metric */}
                    <MetricCard
                        title="Net Income"
                        value={formatCurrency(dashboardData.netIncome, dashboardData.currency)}
                        subtitle={
                            dashboardData.taxSummary
                                ? `After taxes: ${formatCurrency(dashboardData.taxSummary.netIncome, dashboardData.currency)}`
                                : `Revenue minus expenses for ${year}`
                        }
                        icon={<AccountBalanceWalletIcon sx={{ color: '#6366f1', fontSize: 36 }} />}
                        color="#6366f1"
                        large
                    />

                    {/* Key Metrics Row */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                            gap: 3,
                        }}
                    >
                        <MetricCard
                            title="Total Revenue"
                            value={formatCurrency(dashboardData.revenue.totalIncome, dashboardData.currency)}
                            subtitle={`${dashboardData.bookings.count} bookings`}
                            icon={<TrendingUpIcon sx={{ color: '#22c55e', fontSize: 28 }} />}
                            color="#22c55e"
                        />
                        <MetricCard
                            title="Total Expenses"
                            value={formatCurrency(dashboardData.expenses.totalExpenses, dashboardData.currency)}
                            subtitle={`${dashboardData.expenses.byCategory.length} categories`}
                            icon={<TrendingDownIcon sx={{ color: '#ef4444', fontSize: 28 }} />}
                            color="#ef4444"
                        />
                    </Box>

                    {/* Occupancy Metrics */}
                    <Card
                        sx={{
                            border: '3px solid #374151',
                            borderRadius: 0,
                            backgroundColor: '#1a1a2e',
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 900,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    mb: 3,
                                    borderBottom: '2px solid #6366f1',
                                    pb: 1,
                                }}
                            >
                                Occupancy Metrics
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                                    gap: 2,
                                }}
                            >
                                <Box sx={{ p: 2, border: '2px solid #374151' }}>
                                    <Typography
                                        variant="overline"
                                        sx={{ color: 'text.secondary', fontWeight: 700 }}
                                    >
                                        Total Bookings
                                    </Typography>
                                    <Typography
                                        variant="h4"
                                        sx={{ fontWeight: 900, color: '#6366f1' }}
                                    >
                                        {dashboardData.bookings.count}
                                    </Typography>
                                </Box>
                                <Box sx={{ p: 2, border: '2px solid #374151' }}>
                                    <Typography
                                        variant="overline"
                                        sx={{ color: 'text.secondary', fontWeight: 700 }}
                                    >
                                        Nights Hosted
                                    </Typography>
                                    <Typography
                                        variant="h4"
                                        sx={{ fontWeight: 900, color: '#6366f1' }}
                                    >
                                        {dashboardData.bookings.nightsHosted}
                                    </Typography>
                                </Box>
                                <Box sx={{ p: 2, border: '2px solid #374151' }}>
                                    <Typography
                                        variant="overline"
                                        sx={{ color: 'text.secondary', fontWeight: 700 }}
                                    >
                                        Occupancy Rate
                                    </Typography>
                                    <Typography
                                        variant="h4"
                                        sx={{ fontWeight: 900, color: '#6366f1' }}
                                    >
                                        {dashboardData.bookings.occupancyRate}%
                                    </Typography>
                                </Box>
                                <Box sx={{ p: 2, border: '2px solid #374151' }}>
                                    <Typography
                                        variant="overline"
                                        sx={{ color: 'text.secondary', fontWeight: 700 }}
                                    >
                                        Avg Revenue/Night
                                    </Typography>
                                    <Typography
                                        variant="h4"
                                        sx={{ fontWeight: 900, color: '#6366f1' }}
                                    >
                                        {formatCurrency(
                                            dashboardData.bookings.avgRevenuePerNight,
                                            dashboardData.currency
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Revenue Breakdown */}
                    <Card
                        sx={{
                            border: '3px solid #374151',
                            borderRadius: 0,
                            backgroundColor: '#1a1a2e',
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 900,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    mb: 3,
                                    borderBottom: '2px solid #22c55e',
                                    pb: 1,
                                }}
                            >
                                Revenue Breakdown
                            </Typography>
                            <Box
                                sx={{
                                    p: 2,
                                    border: '2px solid #22c55e',
                                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                    <CalendarMonthIcon sx={{ color: '#22c55e', fontSize: 20 }} />
                                    <Typography
                                        variant="overline"
                                        sx={{ color: 'text.secondary', fontWeight: 700 }}
                                    >
                                        Booking Revenue
                                    </Typography>
                                </Stack>
                                <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 900, color: '#22c55e' }}
                                >
                                    {formatCurrency(dashboardData.revenue.bookingRevenue, dashboardData.currency)}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Expense Categories */}
                    <Card
                        sx={{
                            border: '3px solid #374151',
                            borderRadius: 0,
                            backgroundColor: '#1a1a2e',
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 3,
                                    borderBottom: '2px solid #ef4444',
                                    pb: 1,
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 900,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                    }}
                                >
                                    Expense Categories
                                </Typography>
                                <Button
                                    size="small"
                                    onClick={() => navigate({ to: `/${propertyId}/expenses` })}
                                    sx={{
                                        borderRadius: 0,
                                        border: '2px solid #ef4444',
                                        color: '#ef4444',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        fontSize: '0.75rem',
                                        px: 2,
                                        py: 0.5,
                                        '&:hover': {
                                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                            borderColor: '#ef4444',
                                        },
                                    }}
                                >
                                    Manage Expenses
                                </Button>
                            </Box>
                            {dashboardData.expenses.byCategory.length > 0 ? (
                                <Stack spacing={1.5}>
                                    {dashboardData.expenses.byCategory.map((cat) => (
                                        <Box
                                            key={cat.categoryId}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                p: 2,
                                                border: '1px solid #374151',
                                            }}
                                        >
                                            <Typography
                                                variant="body1"
                                                sx={{ fontWeight: 600 }}
                                            >
                                                {cat.name}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: '#ef4444',
                                                    fontFamily: 'monospace',
                                                }}
                                            >
                                                {formatCurrency(cat.amount, dashboardData.currency)}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        textAlign: 'center',
                                        py: 2,
                                    }}
                                >
                                    No expenses recorded for {year}. Click &quot;Manage Expenses&quot; to add some.
                                </Typography>
                            )}
                        </CardContent>
                    </Card>

                    {/* Tax Payment Status */}
                    <TaxPaymentStatusCard propertyId={propertyId} />

                    {/* Tax Section - Show breakdown if configured, prompt if not */}
                    {dashboardData.taxSummary ? (
                        <TaxBreakdownCard
                            data={dashboardData.taxSummary}
                            formatCurrency={formatCurrency}
                        />
                    ) : (
                        <TaxConfigurationPrompt propertyId={propertyId} />
                    )}
                </Stack>
            ) : null}
        </PageContainer>
    );
};

export default FinancialDashboard;
